import { ElementType } from 'api/graphQL/reactiveVars/element'
import React from 'react'
import { useElementItemShallowSelector } from '../ElementItemStore'
import { useSuggestionsShallowSelector } from '../SuggestionStore'
import { useShallowSelector } from '../Workspace2Store'

/**
 * Props for the `useElementToTreeMapper` hook.
 *
 * @interface ElementToTreeMapperProps
 * @property {boolean} isElementsLibraryPage - Indicates if the current page is the elements library page.
 * @property {boolean} isSelected - Indicates if the element is selected.
 * @property {string} elementId - The ID of the element.
 * @property {string} baseElementId - The base ID of the element.
 * @property {string} parentFamilyId - The ID of the parent family.
 * @property {string} elementType - The type of the element.
 * @property {string | null | undefined} [elementAddedToProjectId] - The ID of the element added to the project, if applicable.
 */
interface ElementToTreeMapperProps {
    isElementsLibraryPage: boolean
    isSelected: boolean
    elementId: string
    baseElementId: string
    parentFamilyId: string
    elementType: string
    elementAddedToProjectId?: string | null
}

/**
 * A custom hook that maps an element to the tree structure and performs related actions.
 *
 * @param {ElementToTreeMapperProps} props - The properties required for mapping the element to the tree.
 * @returns {void}
 *
 * @description
 * This hook is responsible for:
 * - Updating the element item value in the store when the element is selected and not on the elements library page.
 * - Resetting track changes data and removing all suggestion data.
 * - Handling suggestion card filter changes.
 * - Setting the tree loading state.
 *
 * @example
 * const props = {
 *   isElementsLibraryPage: false,
 *   isSelected: true,
 *   elementId: '123',
 *   baseElementId: '456',
 *   parentFamilyId: '789',
 *   elementType: 'ProductType',
 *   elementAddedToProjectId: null,
 * };
 * useElementToTreeMapper(props);
 */
export const useElementToTreeMapper = (props: ElementToTreeMapperProps) => {
    const {
        isElementsLibraryPage,
        isSelected,
        elementId,
        baseElementId,
        parentFamilyId,
        elementType,
        elementAddedToProjectId,
    } = props

    const {
        elementItemValue,
        setElementItemValue,
    } = useElementItemShallowSelector((state) => ({
        elementItemValue: state.elementItemValue,
        setElementItemValue: state.setElementItemValue,
    }))
    const {
        setIsSuggestionsDataUpdated,
        removeAllSuggestionData,
        setTrackChangesData,
    } = useSuggestionsShallowSelector((state) => ({
        setIsSuggestionsDataUpdated: state.setIsSuggestionsDataUpdated,
        removeAllSuggestionData: state.removeAllSuggestionData,
        setTrackChangesData: state.setTrackChangesData,
    }))
    const {
        setIsTreeLoading,
        setTreeData,
        setContentItemViewNodes,
    } = useShallowSelector((state) => ({
        setIsTreeLoading: state.actions.setIsTreeLoading,
        setTreeData: state.actions.setTreeData,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
    }))

    const shouldUpdateElement = React.useMemo(() => {
        const alreadySelected = elementItemValue.baseElementId === baseElementId
        return !isElementsLibraryPage && isSelected && !alreadySelected
    }, [
        isElementsLibraryPage,
        isSelected,
        elementItemValue.baseElementId,
        baseElementId,
    ])

    const newElementValue = React.useMemo(
        () => ({
            id: elementId,
            elementId: elementAddedToProjectId || baseElementId,
            parentFamilyId,
            elementType,
            isProductTypeProject: elementType === ElementType.PRODUCTTYPE,
            isProjectProductFamily: Boolean(elementAddedToProjectId),
            actionFlag: elementType,
            baseElementId,
        }),
        [
            elementId,
            elementAddedToProjectId,
            baseElementId,
            parentFamilyId,
            elementType,
        ]
    )

    React.useEffect(() => {
        if (!shouldUpdateElement) return

        const resetTreeAndContent = () => {
            setTreeData([])
            setContentItemViewNodes([])
        }

        const resetSuggestionData = () => {
            removeAllSuggestionData()
            setTrackChangesData({}, [], true) // Clear all track changes data
        }

        resetTreeAndContent()
        resetSuggestionData()
        setIsTreeLoading(true)
        setElementItemValue(newElementValue)
        setIsSuggestionsDataUpdated(true)
    }, [
        shouldUpdateElement,
        newElementValue,
        setElementItemValue,
        removeAllSuggestionData,
        setIsSuggestionsDataUpdated,
        setIsTreeLoading,
        setTrackChangesData,
        setTreeData,
        setContentItemViewNodes,
    ])
}

/**
 * Hook to handle resetting element values after bulk operations
 * This helps prevent the "Not Used" label from appearing after bulk-add
 *
 * @returns {Function} resetElementAfterBulkOperation - Function to reset element state
 */
export const useElementResetAfterBulkOperation = () => {
    const {
        elementItemValue,
        setElementItemValue,
    } = useElementItemShallowSelector((state) => ({
        elementItemValue: state.elementItemValue,
        setElementItemValue: state.setElementItemValue,
    }))

    const { setIsTreeLoading } = useShallowSelector((state) => ({
        setIsTreeLoading: state.actions.setIsTreeLoading,
    }))

    const resetElementAfterBulkOperation = React.useCallback(() => {
        // Reset element values and set tree loading for non-ASSEMBLY types
        if (elementItemValue?.elementType !== 'ASSEMBLY') {
            setElementItemValue({
                ...elementItemValue,
                elementId: '',
                elementType: '',
                baseElementId: '',
            })
            setIsTreeLoading(true)
        }
    }, [elementItemValue, setElementItemValue, setIsTreeLoading])

    return resetElementAfterBulkOperation
}
